import { create } from "zustand";
import { normalizeSymbol } from "./chartLayout.ts";

/**
 * Link groups (SPEC §12.9): colour-coded groups that keep widgets on the same symbol/account
 * (and optionally interval/crosshair/range/drawings). A widget carries one group; retargeting any
 * member retargets the group.
 */
export const LINK_GROUPS = ["red", "green", "blue", "yellow", "purple"] as const;
export type LinkGroup = (typeof LINK_GROUPS)[number];
export type LinkTarget = LinkGroup | "unlinked";

export type GroupState = {
  symbol: string | null;
  accountId: string | null;
  interval: string | null;
  /** Which optional properties this group syncs (symbol is always synced). */
  sync: { account: boolean; interval: boolean; crosshair: boolean; range: boolean; drawings: boolean };
};

const defaultGroup = (): GroupState => ({
  symbol: null,
  accountId: null,
  interval: "1m",
  sync: { account: true, interval: false, crosshair: false, range: false, drawings: false },
});

export type LinkGroupsState = {
  groups: Record<LinkGroup, GroupState>;
  setSymbol: (group: LinkGroup, symbol: string) => void;
  setAccount: (group: LinkGroup, accountId: string | null) => void;
  setInterval: (group: LinkGroup, interval: string) => void;
  setSync: (group: LinkGroup, patch: Partial<GroupState["sync"]>) => void;
};

/** A window's group store (one per page: `useLinkGroups`; tests make one per simulated page). */
export const createLinkGroups = () =>
  create<LinkGroupsState>((set) => ({
    groups: {
      // The red group starts on NQ so a fresh workspace's chart has something to show.
      red: { ...defaultGroup(), symbol: "NQ" },
      green: defaultGroup(),
      blue: defaultGroup(),
      yellow: defaultGroup(),
      purple: defaultGroup(),
    },
    setSymbol: (group, symbol) => {
      set((s) => ({ groups: { ...s.groups, [group]: { ...s.groups[group], symbol } } }));
    },
    setAccount: (group, accountId) => {
      set((s) => ({ groups: { ...s.groups, [group]: { ...s.groups[group], accountId } } }));
    },
    setInterval: (group, interval) => {
      set((s) => ({ groups: { ...s.groups, [group]: { ...s.groups[group], interval } } }));
    },
    setSync: (group, patch) => {
      set((s) => ({
        groups: { ...s.groups, [group]: { ...s.groups[group], sync: { ...s.groups[group].sync, ...patch } } },
      }));
    },
  }));

export const useLinkGroups = createLinkGroups();

/**
 * The groups' symbols as saved with the desktop workspaces (workspacesSync.ts, GPT086): only what
 * the group store holds, never a chart pane's own (possibly stale) symbol. Missing group = not set.
 */
export type LinkSymbols = Partial<Record<LinkGroup, string>>;

/** The group-store surface the workspaces store reads and watches (`useLinkGroups`, or a test's). */
export type LinkGroupsApi = {
  getState: () => LinkGroupsState;
  subscribe: (listener: (state: LinkGroupsState) => void) => () => void;
};

export function linkSymbolsOf(groups: Record<LinkGroup, GroupState>): LinkSymbols {
  const out: LinkSymbols = {};
  for (const g of LINK_GROUPS) {
    const s = groups[g].symbol;
    if (s !== null) out[g] = s;
  }
  return out;
}

/** Known groups with a plausible symbol; undefined when nothing usable is left. */
export function sanitizeLinkSymbols(raw: unknown): LinkSymbols | undefined {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return undefined;
  const out: LinkSymbols = {};
  for (const g of LINK_GROUPS) {
    const s = normalizeSymbol((raw as Record<string, unknown>)[g]);
    if (s !== null) out[g] = s;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

export function sameLinkSymbols(a: LinkSymbols | undefined, b: LinkSymbols | undefined): boolean {
  return LINK_GROUPS.every((g) => (a?.[g] ?? null) === (b?.[g] ?? null));
}

/** Retarget the groups a saved document names; groups it does not name keep their symbol. */
export function restoreLinkSymbols(api: LinkGroupsApi, links: LinkSymbols | undefined): void {
  if (!links) return;
  for (const g of LINK_GROUPS) {
    const s = links[g];
    if (s !== undefined && api.getState().groups[g].symbol !== s) api.getState().setSymbol(g, s);
  }
}

export const LINK_GROUP_COLORS: Record<LinkGroup, string> = {
  red: "#E5484D",
  green: "#1FA37A",
  blue: "#3B82F6",
  yellow: "#F5A524",
  purple: "#A855F7",
};
